import { useId } from 'react'
import { ListBox, Select } from '@heroui/react'
import { limitOf } from '../lib/ticketFields'
import { nameIn } from '../lib/staffText'
import type { VisitField } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'

/** The choice list's "Not set" row: a list item needs a non-empty key. */
const NOT_SET = '__notset__'

/**
 * One declared value a ticket may carry: its label, the control its type asks for,
 * and a way to clear it. Used twice on Reception — in the details typed before a
 * ticket exists, and in a ticket's drawer afterwards — so the two can never ask
 * for the same value in two different ways.
 *
 * **The type is honoured or the box is wrong.** A date gets a date control, a
 * choice its own choices, and a yes-or-no gets two buttons rather than a text box
 * somebody can type "yes" into and have refused. The declaration already says
 * which; guessing is the thing this exists not to do.
 *
 * **Clearing is as reachable as setting.** A value typed onto the wrong customer's
 * visit is the case this has to handle well, and selecting text to delete it is
 * not an answer for a choice list or a pair of buttons. The clear is an icon in the
 * row rather than a word: the label already says what it clears.
 *
 * The label is a span with aria-labelledby rather than a `<label>` wrapping the
 * control: a `<label>` around a control that is not a native input — the choice
 * list is a button — sends a click meant for the control to the label instead.
 */
export function FieldRow({
  field,
  value,
  disabled,
  problem,
  onChange,
}: {
  field: VisitField
  value: string
  disabled?: boolean
  problem: string
  onChange: (value: string) => void
}) {
  const { lang, say } = useStaffText()
  const labelId = useId()
  const label = nameIn(field.name, lang, field.key)
  return (
    <div className="field reception-field">
      <span id={labelId} className="reception-field__label">
        {label}
        {field.personal && <span className="tag">{say('reception.personal')}</span>}
      </span>
      <div className="reception-field__row">
        <FieldControl field={field} value={value} disabled={disabled} labelId={labelId} invalid={problem !== ''} onChange={onChange} />
        {value !== '' && (
          <button
            type="button"
            className="icon-btn"
            disabled={disabled}
            onClick={() => onChange('')}
            aria-label={say('reception.clearLabel', { label })}
            title={say('reception.clearLabel', { label })}
          >
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      {problem !== '' && <small className="reception-error">{problem}</small>}
    </div>
  )
}

function FieldControl({
  field,
  value,
  disabled,
  labelId,
  invalid,
  onChange,
}: {
  field: VisitField
  value: string
  disabled?: boolean
  labelId: string
  invalid: boolean
  onChange: (value: string) => void
}) {
  const { say } = useStaffText()
  const name = useId()

  if (field.type === 'boolean') {
    // Two choices and no third box: a checkbox has two positions and this value
    // has three — yes, no, and not answered — and an unticked box that means "no"
    // is how a visit ends up asserting something nobody asked about. The clear
    // beside it is the third.
    return (
      <div className="seg" role="radiogroup" aria-labelledby={labelId}>
        {(['true', 'false'] as const).map((choice) => (
          <label key={choice}>
            <input type="radio" name={name} checked={value === choice} disabled={disabled} onChange={() => onChange(choice)} />
            <span>{choice === 'true' ? say('common.yes') : say('common.no')}</span>
          </label>
        ))}
      </div>
    )
  }

  if (field.type === 'choice') {
    // HeroUI's Select: the ticket drawer is a HeroUI Modal, which blocks a list portalled outside it.
    return (
      <Select
        variant="secondary"
        value={value || null}
        onChange={(key) => key != null && onChange(key === NOT_SET ? '' : String(key))}
        isDisabled={disabled}
        aria-labelledby={labelId}
        placeholder={say('common.notSet')}
      >
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox>
            {[NOT_SET, ...(field.choices ?? [])].map((choice) => {
              const text = choice === NOT_SET ? say('common.notSet') : choice
              return (
                <ListBox.Item key={choice} id={choice} textValue={text}>
                  {text}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              )
            })}
          </ListBox>
        </Select.Popover>
      </Select>
    )
  }

  return (
    <input
      className="input"
      type={field.type === 'date' ? 'date' : field.type === 'phone' ? 'tel' : 'text'}
      // Text for a number too, rather than type="number": the value crosses the
      // wire as text and is checked as text, and a number input hands back an empty
      // string for anything it cannot parse — a silent clear instead of a refusal.
      inputMode={field.type === 'number' ? 'decimal' : undefined}
      aria-labelledby={labelId}
      aria-invalid={invalid || undefined}
      autoComplete="off"
      disabled={disabled}
      maxLength={field.type === 'date' ? undefined : limitOf(field)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}
